import React, { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "../../../../layout/AuthLayout";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { useRegisterTutorMutation } from "../../../../hooks/api/Tutors/Auth/useRegisterTutorMutation";
import { Eye, EyeOff } from "lucide-react";
import Button from "../../../../components/Button";
import { Checkbox, message, Modal } from "antd";
import { tutorAgreement } from "../../../../agreements";

const TutorSignUpForm: React.FC = () => {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [payload, setPayload] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayload((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const { mutateAsync, isLoading } = useRegisterTutorMutation();

  const handleSubmit = async () => {
    if (!acceptedTerms) {
      message.error("Please accept the Tutor terms and conditions to proceed");
      return;
    }
    try {
      await mutateAsync(payload);
    } catch (error) {
      console.error("Error registering tutor:", error);
    }
  };

  const isFormComplete = (formData: Record<string, string>): boolean => {
    return Object.values(formData).every((value) => value.trim() !== "");
  };

  return (
    <div className="mx-auto">
      <h3 className="text-gray900 text-txs W2STMedium mb-3">
        Create a tutor account
      </h3>

      <div className="flex gap-3 mb-5">
        <div className="w-full">
          <label htmlFor="firstname" className="text-gray700">
            First Name
          </label>
          <input
            id="firstname"
            name="firstName"
            type="text"
            disabled={isLoading}
            value={payload.firstName}
            placeholder="Enter first name"
            className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
            onChange={handleChange}
          />
        </div>
        <div className="w-full">
          <label htmlFor="lastname" className="text-gray700">
            Last Name
          </label>
          <input
            id="lastname"
            name="lastName"
            type="text"
            disabled={isLoading}
            value={payload.lastName}
            placeholder="Enter last name"
            className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="w-full mb-5">
        <label htmlFor="email" className="text-gray700">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          disabled={isLoading}
          value={payload.email}
          placeholder="Enter your email"
          className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
          onChange={handleChange}
        />
      </div>
      <div className="w-full mb-5">
        <label htmlFor="phone" className="text-gray700">
          Phone
        </label>
        {/* <input
              id="phone"
              name="phoneNumber"
              type="tel"
              value={payload.phoneNumber}
              placeholder="Enter your phone number"
              className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
              onChange={handleChange}
            /> */}
        <PhoneInput
          defaultCountry="us"
          className="mt-[13px]"
          name="phoneNumber"
          disabled={isLoading}
          value={payload?.phoneNumber}
          onChange={(phone) => {
            setPayload({
              ...payload,
              phoneNumber: phone,
            });
          }}
        />
      </div>
      <div className="w-full">
        <label htmlFor="password" className="text-gray700">
          Create password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={passwordVisibility ? "text" : "password"}
            value={payload.password}
            disabled={isLoading}
            placeholder="Enter your password"
            className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
            onChange={handleChange}
          />
          <span
            className="absolute right-4 top-5 flex text-gray400 items-center cursor-pointer"
            onClick={() => setPasswordVisibility(!passwordVisibility)}
          >
            {passwordVisibility ? <EyeOff size={20} /> : <Eye size={20} />}
          </span>
        </div>
      </div>
      <Modal
        centered
        width={700}
        footer={null}
        open={showTermsModal}
        onCancel={() => setShowTermsModal(false)}
      >
        <div className="space-y-4">
          <h3 className="text-[20px] W2STMedium">
            Tutor Terms & Responsibilities
          </h3>
          <div className="h-[300px] overflow-y-auto whitespace-pre-wrap text-sm text-gray-700 border rounded-md p-4 bg-gray-50">
            {tutorAgreement}
          </div>
          <div className="flex items-center justify-between">
            {/* <span className="text-sm text-gray-600">
                      View general terms at{" "}
                      <Link to="/terms" className="text-primary600 underline">
                        Terms & Conditions
                      </Link>
                    </span> */}
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                setTimeout(() => {
                  setShowTermsModal(false);
                }, 300);
              }}
            >
              I have read and agree
            </Checkbox>
          </div>
        </div>
      </Modal>
      <div
        className="flex items-center gap-2 my-4 cursor-pointer"
        onChange={(e) => {
          setShowTermsModal(true);
        }}
      >
        <input
          type="checkbox"
          checked={acceptedTerms}
          className="accent-[#A54CE8] h-4 w-4 cursor-pointer"
        />
        <span className="text-sm">
          Click to read the Tutor terms and conditions
        </span>
      </div>
      <div className="block">
        {/* <button
              onClick={handleSubmit}
              className={`block text-center mt-4 py-5 w-full mb-4 rounded-md text-white text-md bg-primary500 ${
                isLoading && "opacity-50"
              }`}
              disabled={isLoading || !Object.values(payload).every(Boolean)}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button> */}
        <Button
          label="Create Account"
          isFormValid={isFormComplete(payload) && acceptedTerms}
          isLoading={isLoading}
          handleSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default TutorSignUpForm;
